'use client';

import type {
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cx } from '../utils/cx';
import { Label } from './label';

export interface SelectOption {
  value: string;
  label: string;
  badge?: string;
  lang?: string;
  disabled?: boolean;
}

export interface SelectProps {
  name: string;
  label?: string;
  hideLabel?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  options: SelectOption[] | 'timezones';
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  className?: string;
  autoComplete?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
}

const TIMEZONE_OPTIONS: SelectOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'GMT/BST' },
  { value: 'Europe/Paris', label: 'CET/CEST' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
  { value: 'Asia/Shanghai', label: 'China Standard Time' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time' },
];

/**
 * Movement (in CSS pixels) above which a touch interaction is treated as a
 * scroll gesture inside the listbox rather than as a tap on an option.
 */
const TAP_SLOP_PX = 10;
const MENU_MAX_HEIGHT_PX = 192;
const MENU_GAP_PX = 8;
const OPTION_MIN_HEIGHT_PX = 44;
const MENU_CHROME_HEIGHT_PX = 18;
const CLIPPING_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'hidden', 'clip', 'overlay']);

type MenuPlacement = 'above' | 'below';

interface MenuLayout {
  placement: MenuPlacement;
  maxHeight: number;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d="m6 9.5 6 6 6-6" />
    </svg>
  );
}

function mergeDescribedBy(...values: Array<string | undefined>): string | undefined {
  const ids = values.flatMap((value) =>
    typeof value === 'string' ? value.trim().split(/\s+/).filter(Boolean) : [],
  );
  return ids.length ? Array.from(new Set(ids)).join(' ') : undefined;
}

function firstEnabledIndex(options: SelectOption[]): number {
  return options.findIndex((option) => !option.disabled);
}

function lastEnabledIndex(options: SelectOption[]): number {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return -1;
}

function nextEnabledIndex(options: SelectOption[], from: number, step: 1 | -1): number {
  if (options.length === 0) return -1;
  let index = from < 0 ? (step > 0 ? -1 : 0) : from;
  for (let attempt = 0; attempt < options.length; attempt += 1) {
    index += step;
    if (index < 0) index = options.length - 1;
    if (index > options.length - 1) index = 0;
    if (!options[index]?.disabled) return index;
  }
  return from >= 0 && !options[from]?.disabled ? from : -1;
}

function getUnobscuredBottom(
  element: HTMLElement,
  clippingAncestor: HTMLElement,
  top: number,
  bottom: number,
): number {
  if (bottom - top <= 1) return bottom;

  const elementBounds = element.getBoundingClientRect();
  const visualViewport = window.visualViewport;
  const viewportLeft = visualViewport?.offsetLeft ?? 0;
  const viewportRight = viewportLeft + (visualViewport?.width ?? window.innerWidth);
  const pointX = Math.min(
    Math.max(elementBounds.left + elementBounds.width / 2, viewportLeft),
    Math.max(viewportLeft, viewportRight - 1),
  );
  const pointY = bottom - 1;
  let unobscuredBottom = bottom;

  // A fixed or stacked footer can cover part of a scroll container without
  // changing that container's bounds. Inspect the painted stack at its lower
  // edge and reserve space occupied by sibling content above the container.
  for (const candidate of document.elementsFromPoint(pointX, pointY)) {
    if (!(candidate instanceof HTMLElement)) continue;

    if (candidate === clippingAncestor || clippingAncestor.contains(candidate)) {
      continue;
    }

    // Once an ancestor containing the clipping region is reached, subsequent
    // elements are behind the region and cannot obscure the select menu.
    if (candidate.contains(clippingAncestor)) break;

    const candidateBounds = candidate.getBoundingClientRect();
    const coversPoint =
      candidateBounds.left <= pointX &&
      candidateBounds.right >= pointX &&
      candidateBounds.top <= pointY &&
      candidateBounds.bottom > pointY;

    if (coversPoint) {
      unobscuredBottom = Math.min(
        unobscuredBottom,
        Math.max(top, candidateBounds.top),
      );
    }
  }

  return unobscuredBottom;
}

function getVisibleVerticalBounds(element: HTMLElement): { top: number; bottom: number } {
  const visualViewport = window.visualViewport;
  let top = visualViewport?.offsetTop ?? 0;
  let bottom = top + (visualViewport?.height ?? window.innerHeight);
  let ancestor = element.parentElement;
  let clippingAncestor: HTMLElement | null = null;

  while (
    ancestor &&
    ancestor !== document.body &&
    ancestor !== document.documentElement
  ) {
    const overflowY = window.getComputedStyle(ancestor).overflowY;

    if (CLIPPING_OVERFLOW_VALUES.has(overflowY)) {
      clippingAncestor ??= ancestor;
      const ancestorBounds = ancestor.getBoundingClientRect();
      top = Math.max(top, ancestorBounds.top);
      bottom = Math.min(bottom, ancestorBounds.bottom);
    }

    ancestor = ancestor.parentElement;
  }

  if (clippingAncestor) {
    bottom = getUnobscuredBottom(element, clippingAncestor, top, bottom);
  }

  return { top, bottom };
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    name,
    label,
    hideLabel = false,
    placeholder,
    value,
    defaultValue,
    onChange,
    options,
    required = false,
    disabled = false,
    helperText,
    className,
    autoComplete,
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
  },
  ref,
) {
  const autoId = useId();
  const triggerId = id ?? `select-${name}-${autoId}`;
  const listboxId = `${triggerId}__listbox`;
  const helperId = helperText ? `${triggerId}__helper` : undefined;
  const optionList = useMemo(
    () => (typeof options === 'string' && options === 'timezones' ? TIMEZONE_OPTIONS : options),
    [options],
  );
  const controlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const currentValue = controlled ? value : internalValue;
  const selectedIndex = optionList.findIndex((option) => option.value === currentValue);
  const selectedOption = selectedIndex >= 0 ? optionList[selectedIndex] : undefined;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuLayout, setMenuLayout] = useState<MenuLayout>({
    placement: 'below',
    maxHeight: MENU_MAX_HEIGHT_PX,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  // iOS Safari commits a selection on `pointerup`; this suppresses the click
  // event that may follow so `onChange` is not emitted twice.
  const suppressClickRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number; id: number } | null>(null);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ref],
  );
  const describedBy = mergeDescribedBy(ariaDescribedBy, helperId);

  const updateMenuLayout = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const triggerBounds = trigger.getBoundingClientRect();
    const visibleBounds = getVisibleVerticalBounds(trigger);
    const availableAbove = Math.max(
      0,
      triggerBounds.top - visibleBounds.top - MENU_GAP_PX,
    );
    const availableBelow = Math.max(
      0,
      visibleBounds.bottom - triggerBounds.bottom - MENU_GAP_PX,
    );
    const estimatedHeight =
      optionList.length * OPTION_MIN_HEIGHT_PX + MENU_CHROME_HEIGHT_PX;
    const measuredHeight = listboxRef.current?.scrollHeight;
    const desiredHeight = Math.min(
      MENU_MAX_HEIGHT_PX,
      measuredHeight && measuredHeight > 0 ? measuredHeight : estimatedHeight,
    );
    const placement: MenuPlacement =
      availableBelow < desiredHeight && availableAbove > availableBelow ? 'above' : 'below';
    const availableHeight = placement === 'above' ? availableAbove : availableBelow;
    const maxHeight = Math.max(
      0,
      Math.min(MENU_MAX_HEIGHT_PX, Math.floor(availableHeight)),
    );

    setMenuLayout((current) =>
      current.placement === placement && current.maxHeight === maxHeight
        ? current
        : { placement, maxHeight },
    );
  }, [optionList.length]);

  const closeMenu = useCallback((refocus = false) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(
    (preferredIndex?: number) => {
      const fallbackIndex =
        selectedIndex >= 0 && !optionList[selectedIndex]?.disabled
          ? selectedIndex
          : firstEnabledIndex(optionList);
      const nextIndex =
        typeof preferredIndex === 'number' && preferredIndex >= 0 ? preferredIndex : fallbackIndex;
      updateMenuLayout();
      setActiveIndex(nextIndex);
      setOpen(true);
    },
    [optionList, selectedIndex, updateMenuLayout],
  );

  const selectOption = useCallback(
    (option: SelectOption | undefined, refocus = false) => {
      if (!option || option.disabled) return;
      if (!controlled) setInternalValue(option.value);
      onChange?.(option.value);
      setOpen(false);
      setActiveIndex(-1);
      if (refocus) triggerRef.current?.focus();
    },
    [controlled, onChange],
  );

  // Keep the ref cache in step with the rendered option count.
  useEffect(() => {
    optionRefs.current.length = optionList.length;
  }, [optionList]);

  useEffect(() => {
    if (!open) {
      suppressClickRef.current = false;
      pointerStartRef.current = null;
    }
  }, [open]);

  // Reposition the menu when mobile browser chrome, orientation, or a scroll
  // changes the usable area. Capture-phase scroll covers nested scroll panels.
  useEffect(() => {
    if (!open) return;

    let animationFrame: number | undefined;
    const scheduleLayoutUpdate = () => {
      if (animationFrame !== undefined) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = undefined;
        updateMenuLayout();
      });
    };
    const visualViewport = window.visualViewport;

    updateMenuLayout();
    window.addEventListener('resize', scheduleLayoutUpdate);
    window.addEventListener('scroll', scheduleLayoutUpdate, true);
    visualViewport?.addEventListener('resize', scheduleLayoutUpdate);
    visualViewport?.addEventListener('scroll', scheduleLayoutUpdate);

    return () => {
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', scheduleLayoutUpdate);
      window.removeEventListener('scroll', scheduleLayoutUpdate, true);
      visualViewport?.removeEventListener('resize', scheduleLayoutUpdate);
      visualViewport?.removeEventListener('scroll', scheduleLayoutUpdate);
    };
  }, [open, updateMenuLayout]);

  // Keep the active option visible while navigating with the keyboard.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideInteraction = (event: Event) => {
      const target = event.target as Node | null;
      if (target && rootRef.current?.contains(target)) return;
      setOpen(false);
      setActiveIndex(-1);
    };
    // Older iOS Safari builds do not expose PointerEvent; fall back to the
    // mouse/touch pair so the menu still dismisses on an outside tap.
    const eventNames: string[] =
      typeof window !== 'undefined' && 'PointerEvent' in window
        ? ['pointerdown']
        : ['mousedown', 'touchstart'];
    eventNames.forEach((eventName) =>
      document.addEventListener(eventName, closeOnOutsideInteraction, true),
    );
    return () =>
      eventNames.forEach((eventName) =>
        document.removeEventListener(eventName, closeOnOutsideInteraction, true),
      );
  }, [open]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'Spacebar': {
        event.preventDefault();
        if (!open) openMenu();
        else selectOption(activeIndex >= 0 ? optionList[activeIndex] : undefined, true);
        break;
      }
      case 'ArrowDown': {
        event.preventDefault();
        if (!open) openMenu();
        else setActiveIndex((current) => nextEnabledIndex(optionList, current, 1));
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) openMenu(lastEnabledIndex(optionList));
        else setActiveIndex((current) => nextEnabledIndex(optionList, current, -1));
        break;
      }
      case 'Home': {
        if (!open) break;
        event.preventDefault();
        setActiveIndex(firstEnabledIndex(optionList));
        break;
      }
      case 'End': {
        if (!open) break;
        event.preventDefault();
        setActiveIndex(lastEnabledIndex(optionList));
        break;
      }
      case 'Escape': {
        if (!open) break;
        event.preventDefault();
        closeMenu(true);
        break;
      }
      case 'Tab': {
        if (open) closeMenu();
        break;
      }
      default:
        break;
    }
  };

  const handleOptionPointerDown = (event: ReactPointerEvent<HTMLLIElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
  };

  const handleOptionPointerCancel = () => {
    pointerStartRef.current = null;
  };

  const handleOptionPointerUp = (
    event: ReactPointerEvent<HTMLLIElement>,
    option: SelectOption,
  ) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (event.button > 0) return;
    if (start && start.id === event.pointerId) {
      const movedX = event.clientX - start.x;
      const movedY = event.clientY - start.y;
      // The listbox scrolls on touch; only commit when the finger stayed put.
      if (Math.sqrt(movedX * movedX + movedY * movedY) > TAP_SLOP_PX) return;
    }
    suppressClickRef.current = true;
    selectOption(option, event.pointerType === 'mouse');
  };

  const handleOptionClick = (option: SelectOption) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    selectOption(option, true);
  };

  return (
    <div ref={rootRef} className="w-full min-w-0">
      {label && !hideLabel ? (
        <Label htmlFor={triggerId} required={required}>
          {label}
        </Label>
      ) : null}
      {label && hideLabel ? (
        <label htmlFor={triggerId} className="sr-only">
          {label}
        </label>
      ) : null}
      <div className={cx('relative', label && !hideLabel && 'mt-1.5')}>
        <button
          ref={setTriggerRef}
          type="button"
          id={triggerId}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-describedby={describedBy}
          aria-label={!label ? ariaLabel : undefined}
          aria-required={required || undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
          }
          disabled={disabled}
          onClick={() => (open ? closeMenu() : openMenu())}
          onKeyDown={handleKeyDown}
          className={cx(
            'group relative flex min-h-12 w-full min-w-0 touch-manipulation select-none appearance-none items-center justify-between gap-2 rounded-2xl border border-[#e3ded3] bg-white px-3 py-2.5 text-start text-sm text-slate-900 [-webkit-tap-highlight-color:transparent]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2',
            className,
          )}
        >
          <span className="flex min-w-0 items-center justify-start gap-2.5 px-2 text-start">
            <span
              lang={selectedOption?.lang}
              dir="auto"
              className={cx(
                'min-w-0 flex-1 truncate text-start',
                selectedOption ? 'font-semibold text-slate-900' : 'text-slate-400',
              )}
            >
              {selectedOption?.label ?? placeholder ?? 'Select…'}
            </span>
          </span>
          <span
            aria-hidden="true"
            className={cx(
              'pointer-events-none flex h-6 w-6 shrink-0 items-center justify-center',
              disabled
                ? 'text-slate-300'
                : open
                  ? 'text-violet-500'
                  : 'text-slate-400 group-hover:text-slate-600',
            )}
          >
            <ChevronDownIcon
              className={cx(
                'h-4 w-4 transition-transform duration-200 ease-out motion-reduce:transition-none',
                open && 'rotate-180',
              )}
            />
          </span>
        </button>
        {open ? (
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label={label ?? ariaLabel}
            style={{ maxHeight: menuLayout.maxHeight }}
            className={cx(
              'absolute inset-x-0 z-50 max-h-72 touch-pan-y overflow-y-auto overscroll-contain rounded-2xl border border-[#e3ded3] bg-white p-2 [-webkit-overflow-scrolling:touch]',
              menuLayout.placement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2',
            )}
          >
            {optionList.map((option, index) => (
              <li
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={option.value === currentValue}
                aria-disabled={option.disabled || undefined}
                onMouseEnter={() => {
                  if (!option.disabled) setActiveIndex(index);
                }}
                onPointerDown={handleOptionPointerDown}
                onPointerUp={(event) => {
                  if (option.disabled) return;
                  handleOptionPointerUp(event, option);
                }}
                onPointerCancel={handleOptionPointerCancel}
                onClick={() => {
                  if (option.disabled) return;
                  handleOptionClick(option);
                }}
                className={cx(
                  'flex min-h-11 cursor-pointer touch-manipulation select-none items-center justify-start rounded-xl px-3 py-2 text-start text-sm [-webkit-tap-highlight-color:transparent]',
                  index === activeIndex && option.value !== currentValue && 'bg-slate-50',
                  option.value === currentValue && 'bg-violet-50 font-semibold text-violet-900',
                  option.disabled && 'pointer-events-none opacity-50',
                )}
              >
                <span lang={option.lang} dir="auto" className="min-w-0 truncate text-start">
                  {option.label}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <input type="hidden" name={name} value={currentValue} autoComplete={autoComplete} readOnly />
      </div>
      {helperText ? (
        <p id={helperId} className="mt-2 ps-1 text-xs leading-5 text-slate-500">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});

Select.displayName = 'Select';